import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getToken } from '../utils/auth';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import './ProductDetail.css';

const API_BASE = 'http://localhost:5001';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getToken());

  useEffect(() => {
    const syncAuth = () => setIsAuthenticated(!!getToken());
    syncAuth();
    window.addEventListener('storage', syncAuth);

    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/products/${id}`);
        setProduct(res.data);
        setError('');
        const rawAvailable = res.data?.stock ?? res.data?.quantity;
        const available = rawAvailable === undefined || rawAvailable === null || rawAvailable === ''
          ? null
          : Number(rawAvailable);
        if (available !== null && Number.isFinite(available)) {
          setQuantity(available > 0 ? 1 : 0);
        } else {
          setQuantity(1);
        }
      } catch (err) {
        setError('Erreur lors du chargement du produit');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();

    return () => window.removeEventListener('storage', syncAuth);
  }, [id]);

  const handleChange = (e) => {
    const requested = Math.max(1, Number(e.target.value) || 1);
    const rawAvailable = product?.stock ?? product?.quantity;
    const available = rawAvailable === undefined || rawAvailable === null || rawAvailable === ''
      ? null
      : Number(rawAvailable);
    if (available !== null && Number.isFinite(available)) {
      if (available <= 0) {
        setQuantity(0);
        return;
      }
      setQuantity(Math.min(requested, available));
    } else {
      setQuantity(requested);
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Veuillez vous connecter pour ajouter au panier.');
        navigate('/login');
        return;
      }

      const rawAvailable = product?.stock ?? product?.quantity;
      const available = rawAvailable === undefined || rawAvailable === null || rawAvailable === ''
        ? null
        : Number(rawAvailable);
      let finalQuantity = quantity;

      if (available !== null && Number.isFinite(available)) {
        if (available <= 0) {
          setError('Ce produit est en rupture de stock.');
          return;
        }
        finalQuantity = Math.min(Math.max(1, quantity), available);
        if (finalQuantity !== quantity) {
          setQuantity(finalQuantity);
        }
      }

      await axios.post(`${API_BASE}/api/orders`, {
        productId: product._id,
        quantity: finalQuantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Ajouté au panier : ${product.name} x ${finalQuantity}`);
    } catch (err) {
      const message = err.response?.data?.error || "Erreur lors de l'ajout au panier";
      setError(message);
    }
  };

  const handleClose = () => {
    const historyState = window.history.state;
    const canGoBack = historyState && historyState.idx > 0;
    const originCategory = location.state?.selectedCategory;
    const originSearch = location.state?.searchParams || '';

    if (location.state?.fromProductList) {
      const targetPath = `/products${originSearch}`;
      navigate(targetPath, { state: { selectedCategory: originCategory ?? null } });
      return;
    }

    if (canGoBack) {
      navigate(-1);
      return;
    }

    if (window.opener) {
      window.close();
      return;
    }

    navigate('/products');
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!product) {
    return <div>Produit introuvable.</div>;
  }

  const packQuantityDisplay = (() => {
    if (product?.packQuantity === null || product?.packQuantity === undefined) return '';
    if (typeof product.packQuantity === 'number' && Number.isFinite(product.packQuantity)) {
      return product.packQuantity.toString();
    }
    return product.packQuantity;
  })();

  const packInfoParts = [
    packQuantityDisplay,
    (product?.volume || '').trim(),
    (product?.alcoholPercentage || '').trim(),
    (product?.countryCode || '').trim()
  ].filter(Boolean);

  const detailMeta = [
    product?.variety ? { label: 'Variété', value: product.variety } : null,
    packInfoParts.length ? { label: 'Conditionnement', value: packInfoParts.join(' / ') } : null,
    product?.barcode ? { label: 'Code barre', value: product.barcode } : null,
    product?.weight ? { label: 'Poids', value: product.weight } : null,
    product?.pallet ? { label: 'Palettisation', value: product.pallet } : null
  ].filter(Boolean);

  const rawAvailableStock = product?.stock ?? product?.quantity;
  const availableStock = rawAvailableStock === undefined || rawAvailableStock === null || rawAvailableStock === ''
    ? null
    : Number(rawAvailableStock);
  const outOfStock = availableStock !== null && Number.isFinite(availableStock) && availableStock <= 0;
  const displayQuantity = availableStock !== null && Number.isFinite(availableStock)
    ? Math.min(quantity, Math.max(availableStock, 0))
    : quantity;
  const imageUrl = product.image ? (product.image.startsWith('http') ? product.image : `${API_BASE}${product.image}`) : null;

  return (
    <div className="product-detail">
      <div className="product-detail__card">
        <button type="button" className="product-detail__close" onClick={handleClose} aria-label="Fermer la fiche produit">×</button>
        <div className="product-detail__media">
          <div className="product-detail__image-wrapper">
            {imageUrl ? (
              <img src={imageUrl} alt={product.name} className="product-detail__image" />
            ) : (
              <div className="product-detail__image product-detail__image--placeholder">Aucune image</div>
            )}
          </div>
          <div className="product-detail__info">
            <h2 className="product-detail__title">{product.name}</h2>
            {product.description && <p className="product-detail__description">{product.description}</p>}
            {detailMeta.length > 0 && (
              <dl className="product-detail__meta">
                {detailMeta.map((item) => (
                  <div key={`${item.label}-${item.value}`} className="product-detail__meta-row">
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {isAuthenticated ? (
              product.price !== undefined ? (<p className="product-detail__price">Prix : {product.price} €</p>) : null
            ) : (
              <p className="product-detail__price product-detail__price--locked">connectez vous pour voir les prix</p>
            )}
            {availableStock !== null && Number.isFinite(availableStock) && (
              <p className="product-detail__stock">Stock disponible : {Math.max(availableStock, 0)}</p>
            )}
          </div>
        </div>
        <form onSubmit={handleAddToCart} className="product-detail__form">
          <label className="product-detail__label" htmlFor="quantity">Quantité</label>
          <input id="quantity" type="number" min={1} value={displayQuantity} onChange={handleChange} className="product-detail__input" disabled={outOfStock} />
          <button type="submit" className="product-detail__submit" disabled={outOfStock}>
            {outOfStock ? 'Indisponible' : 'Ajouter au panier'}
          </button>
        </form>
        {success && <p className="product-detail__message product-detail__message--success">{success}</p>}
        {error && <p className="product-detail__message product-detail__message--error">{error}</p>}
      </div>
    </div>
  );
}

export default ProductDetail;
